import React, { useState, useEffect, useRef } from "react"
import styles from "./TextBox.module.css"

interface TextBoxProps {
    data: { fieldName: string; value: string }
    onDoneEditing: (fieldName: string, newValue: string) => void
    canEdit: boolean
    maxLength: number // New prop for character limit
    extraStyle?: React.CSSProperties
}

const TextBox: React.FC<TextBoxProps> = ({ data, onDoneEditing, canEdit, maxLength, extraStyle = {} }) => {
    const [value, setValue] = useState<string>(data.value.length > maxLength ? data.value.substring(0, maxLength) : data.value)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    useEffect(() => {
        setValue(data.value.length > maxLength ? data.value.substring(0, maxLength) : data.value)
    }, [data.value, maxLength])

    const adjustHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto"
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
        }
    }

    useEffect(() => {
        adjustHeight()
    }, [value, canEdit])

    useEffect(() => {
        window.addEventListener("resize", adjustHeight)
        return () => {
            window.removeEventListener("resize", adjustHeight)
        }
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value.length <= maxLength) {
            setValue(e.target.value)
        }
    }

    const handleBlur = () => {
        if (value !== data.value) {
            onDoneEditing(data.fieldName, value)
        }
    }

    return (
        <div className={styles.textBoxContainer}>
            <textarea
                ref={textareaRef}
                className={styles.textInput}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={1}
                maxLength={maxLength}
                style={{ height: "auto", overflow: "auto", ...extraStyle }} // Ensure initial height is auto and allow overflow
                readOnly={!canEdit} // Make textarea read-only if not editable
            />
            {canEdit && (
                <div className={styles.charCount}>
                    {value.length}/{maxLength}
                </div>
            )}
        </div>
    )
}

export default TextBox
